import { useCustom } from "@refinedev/core";
import { List } from "@refinedev/antd";
import {
  Table,
  Tag,
  Button,
  Space,
  Modal,
  Select,
  notification,
  Typography,
  Input,
  Divider,
  Descriptions,
  Dropdown,
  Checkbox,
  Tooltip,
  Card,
  Grid,
  Segmented,
  theme,
} from "antd";
import {
  SettingOutlined,
  AppstoreOutlined,
  BarsOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type { FilterDropdownProps } from "antd/es/table/interface";
import { useState, useRef, useMemo, useEffect } from "react";
import debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";
import type { InBasketItem, InBasketResult, ActivityPath } from "../../types";
import { axiosInstance } from "../../providers/dataProvider";
import { PageTourButton } from "../../features/userGuide";

const { useBreakpoint } = Grid;

const { Text, Title } = Typography;
const { TextArea } = Input;

type ViewMode = "table" | "card";

// Map InBasket item type values to translation keys
const TYPE_TRANSLATION_MAP: Record<string, string> = {
  "Workflow Task": "workflow.workflowTask",
};

export const InBasket: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const screens = useBreakpoint();

  // Responsive values
  const isMobile = !screens.md;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InBasketItem | null>(null);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [comments, setComments] = useState("");
  const [completing, setCompleting] = useState(false);

  // Track if user manually changed view mode
  const userOverrodeViewMode = useRef(false);
  const [manualViewMode, setManualViewMode] = useState<ViewMode | null>(null);

  // Compute effective view mode: user choice takes priority, otherwise auto-detect
  const viewMode: ViewMode = manualViewMode ?? (isMobile ? "card" : "table");

  // Handler for manual view mode change
  const handleViewModeChange = (mode: ViewMode) => {
    userOverrodeViewMode.current = true;
    setManualViewMode(mode);
  };

  // Global search state
  const [searchText, setSearchText] = useState("");
  const [searchField, setSearchField] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFieldQuery, setSearchFieldQuery] = useState("");

  // Searchable field options for the Field Selector dropdown
  const searchFieldOptions = [
    { value: "", label: t("table.allFields", "Tất cả") },
    { value: "keyed_name", label: t("table.sourceItem") },
    { value: "name", label: t("table.activity") },
    { value: "instructions", label: t("table.instructions") },
  ];

  // Debounced search: updates the query params sent to API
  const applySearch = useMemo(
    () =>
      debounce((text: string, field: string) => {
        setSearchQuery(text);
        setSearchFieldQuery(field);
        setPage(1);
      }, 400),
    [],
  );

  useEffect(() => {
    return () => {
      applySearch.cancel();
    };
  }, [applySearch]);

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    applySearch(e.target.value, searchField);
  };

  const onSearchFieldChange = (value: string) => {
    setSearchField(value);
    if (searchText) {
      applySearch(searchText, value);
    }
  };

  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    "type",
    "sourceItem",
    "name",
    "startDate",
    "dueDate",
    "state",
    "assignedTo",
    "instructions",
    "actions",
  ]);

  const { data, isLoading, refetch } = useCustom<InBasketResult>({
    url: "/workflow/inbasket",
    method: "get",
    config: {
      query: {
        page,
        pageSize,
        ...(searchQuery ? { search: searchQuery } : {}),
        ...(searchQuery && searchFieldQuery
          ? { searchField: searchFieldQuery }
          : {}),
      },
    },
  });

  // Use activityId (not id) for getting vote paths
  const { data: pathsData, isLoading: pathsLoading } = useCustom<
    ActivityPath[]
  >({
    url: `/workflow/activities/${selectedItem?.activityId}/paths`,
    method: "get",
    queryOptions: {
      enabled: !!selectedItem?.activityId,
    },
  });

  const paths = pathsData?.data || [];
  const inbasketData = data?.data;

  const handleComplete = async () => {
    if (!selectedItem || !selectedPath) return;

    // Find the selected path object to get all required fields
    const pathObj = paths.find((p) => p.id === selectedPath);
    if (!pathObj) {
      notification.error({
        message: t("workflow.pathNotFound"),
        description: t(
          "workflow.pathNotFoundDescription",
          "Không tìm thấy đường dẫn workflow. Vui lòng thử lại.",
        ),
        placement: "topRight",
        duration: 5,
      });
      return;
    }

    setCompleting(true);
    try {
      const response = await axiosInstance.post("workflow/complete", {
        activityId:
          pathObj.activityId || selectedItem.activityId || selectedItem.id,
        activityAssignmentId:
          pathObj.activityAssignmentId ||
          selectedItem.activityAssignmentId ||
          "",
        pathId: selectedPath,
        pathName: pathObj.name || pathObj.label || "",
        comments: comments || undefined,
      });
      const successMessage = response?.data?.message;
      notification.success({
        message: t("workflow.taskCompleted"),
        description:
          successMessage ||
          t(
            "workflow.taskCompletedDescription",
            "Công việc đã được duyệt và chuyển sang bước tiếp theo.",
          ),
        placement: "topRight",
        duration: 5,
      });
      handleCloseModal();
      refetch();
    } catch (error: unknown) {
      const serverMessage = (error as Error)?.message;
      // Aras validation errors contain HTML (styled lists of missing fields).
      // Strip the method prefix (e.g. "Opp_ValidateOnVote: ") and render
      // the remaining HTML so users see exactly which fields are missing.
      let description: React.ReactNode = t(
        "workflow.taskFailedDescription",
        "Có lỗi xảy ra khi duyệt công việc. Vui lòng thử lại.",
      );
      if (serverMessage) {
        let htmlContent = serverMessage
          .replace(/^[A-Za-z_]+:\s*/, "")
          .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "");
        // Aras labels use bilingual format "Vietnamese / English".
        // Show only the language matching the current locale.
        if (i18n.language?.startsWith("vi")) {
          htmlContent = htmlContent.replace(
            /(<b>)([^<]*?)\s*\/\s*[^<]*?(<\/b>)/gi,
            "$1$2$3",
          );
        } else {
          htmlContent = htmlContent.replace(
            /(<b>)[^<]*?\s*\/\s*([^<]*?)(<\/b>)/gi,
            "$1$2$3",
          );
        }
        description = <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
      }
      notification.error({
        message: t("workflow.taskFailed"),
        description,
        placement: "topRight",
        duration: 8,
      });
    } finally {
      setCompleting(false);
    }
  };

  const handleCloseModal = () => {
    setCompleteModalVisible(false);
    setSelectedItem(null);
    setSelectedPath(null);
    setComments("");
  };

  const columnConfig = [
    { key: "type", label: t("table.type") },
    { key: "sourceItem", label: t("table.sourceItem") },
    { key: "name", label: t("table.activity") },
    { key: "startDate", label: t("table.startDate") },
    { key: "dueDate", label: t("table.dueDate") },
    { key: "state", label: t("table.state") },
    { key: "assignedTo", label: t("table.assignedTo") },
    { key: "instructions", label: t("table.instructions") },
    { key: "actions", label: t("table.actions") },
  ];

  const handleColumnVisibilityChange = (
    columnKey: string,
    checked: boolean,
  ) => {
    if (checked) {
      setVisibleColumns([...visibleColumns, columnKey]);
    } else {
      setVisibleColumns(visibleColumns.filter((key) => key !== columnKey));
    }
  };

  const columnSettingsMenu = {
    items: columnConfig.map((col) => ({
      key: col.key,
      label: (
        <Checkbox
          checked={visibleColumns.includes(col.key)}
          onChange={(e) =>
            handleColumnVisibilityChange(col.key, e.target.checked)
          }
        >
          {col.label}
        </Checkbox>
      ),
    })),
  };

  // Derive state filter options from actual data instead of hardcoding
  const stateOptions = useMemo(() => {
    const states = [...new Set((inbasketData?.items || []).map((i) => i.state).filter(Boolean))];
    return states.map((s) => ({ label: s!, value: s! }));
  }, [inbasketData]);

  // Reusable text filter dropdown for column headers
  const textFilterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
    <div style={{ padding: 8 }}>
      <Input
        value={selectedKeys[0] as string}
        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => confirm()}
        placeholder={t("table.searchPlaceholder", "Tìm kiếm...")}
        style={{ width: 200, marginBottom: 8, display: "block" }}
      />
      <Space>
        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
          {t("buttons.filter", "Lọc")}
        </Button>
        <Button onClick={() => { clearFilters?.(); confirm(); }} size="small" style={{ width: 90 }}>
          {t("buttons.clear", "Xóa")}
        </Button>
      </Space>
    </div>
  );

  const columns = [
    {
      title: t("table.type"),
      dataIndex: "type",
      key: "type",
      width: 120,
      sorter: true,
      filterDropdown: textFilterDropdown,
      onFilter: (value: unknown, record: InBasketItem) => {
        const displayed = t(TYPE_TRANSLATION_MAP[record.type] || record.type, record.type);
        return displayed.toLowerCase().includes(String(value).toLowerCase());
      },
      render: (value: string) => (
        <Tag>{t(TYPE_TRANSLATION_MAP[value] || value, value)}</Tag>
      ),
    },
    {
      title: t("table.sourceItem"),
      key: "sourceItem",
      dataIndex: "sourceItemName",
      width: 120,
      sorter: true,
      filterDropdown: textFilterDropdown,
      onFilter: (value: unknown, record: InBasketItem) =>
        (record.sourceItemName ?? "").toLowerCase().includes(String(value).toLowerCase()),
      render: (_: unknown, record: InBasketItem) => {
        if (record.sourceItemName) {
          // Create a link to the source item if we have type and id
          const linkPath =
            record.sourceItemType && record.sourceItemId
              ? `/${record.sourceItemType.toLowerCase()}s/show/${
                  record.sourceItemId
                }`
              : null;
          return linkPath ? (
            <a href={linkPath}>{record.sourceItemName}</a>
          ) : (
            <Text strong>{record.sourceItemName}</Text>
          );
        }
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: t("table.activity"),
      dataIndex: "name",
      key: "name",
      sorter: true,
      filterDropdown: textFilterDropdown,
      onFilter: (value: unknown, record: InBasketItem) =>
        (record.name ?? "").toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: t("table.startDate"),
      dataIndex: "startDate",
      key: "startDate",
      width: 120,
      sorter: true,
      render: (value: string) =>
        value ? (
          new Date(value).toLocaleDateString()
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: t("table.dueDate"),
      dataIndex: "dueDate",
      key: "dueDate",
      width: 120,
      sorter: true,
      render: (value: string) =>
        value ? (
          new Date(value).toLocaleDateString()
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: t("table.state"),
      dataIndex: "state",
      key: "state",
      width: 100,
      sorter: true,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Select
            style={{ width: 200, marginBottom: 8, display: "block" }}
            allowClear
            value={selectedKeys[0] as string}
            onChange={(val) => setSelectedKeys(val ? [val] : [])}
            placeholder={t("table.selectFilter", "Chọn...")}
            options={stateOptions}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              {t("buttons.filter", "Lọc")}
            </Button>
            <Button onClick={() => { clearFilters?.(); confirm(); }} size="small" style={{ width: 90 }}>
              {t("buttons.clear", "Xóa")}
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value: unknown, record: InBasketItem) =>
        record.state === String(value),
      render: (value: string) => (
        <Tag color={value === "Active" ? "green" : "default"}>{value}</Tag>
      ),
    },
    {
      title: t("table.assignedTo"),
      dataIndex: "assignedTo",
      key: "assignedTo",
      width: 120,
      sorter: true,
      filterDropdown: textFilterDropdown,
      onFilter: (value: unknown, record: InBasketItem) =>
        (record.assignedTo ?? "").toLowerCase().includes(String(value).toLowerCase()),
      render: (value: string) =>
        value ? value : <Text type="secondary">-</Text>,
    },
    {
      title: t("table.instructions"),
      dataIndex: "instructions",
      key: "instructions",
      ellipsis: true,
      sorter: true,
      render: (value: string) =>
        value ? value : <Text type="secondary">-</Text>,
    },
    {
      title: t("table.actions"),
      key: "actions",
      width: isMobile ? 80 : 100,
      fixed: isMobile ? undefined : ("right" as const),
      render: (_: unknown, record: InBasketItem) => (
        <Button
          data-tour="complete-task-button"
          type="primary"
          size="small"
          onClick={() => {
            setSelectedItem(record);
            setCompleteModalVisible(true);
          }}
        >
          {t("actions.complete")}
        </Button>
      ),
    },
  ];

  const filteredColumns = columns.filter((col) =>
    visibleColumns.includes(col.key),
  );

  // Render a single task card for mobile/card view
  const renderTaskCard = (item: InBasketItem) => {
    // Create a link to the source item if we have type and id
    const linkPath =
      item.sourceItemType && item.sourceItemId
        ? `/${item.sourceItemType.toLowerCase()}s/show/${item.sourceItemId}`
        : null;

    return (
      <Card
        key={item.id}
        size="small"
        style={{ marginBottom: 16 }}
        actions={[
          <Button
            key="complete"
            type="primary"
            onClick={() => {
              setSelectedItem(item);
              setCompleteModalVisible(true);
            }}
          >
            {t("actions.complete")}
          </Button>,
        ]}
      >
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Space>
            <Tag>
              {t(TYPE_TRANSLATION_MAP[item.type] || item.type, item.type)}
            </Tag>
            {linkPath ? (
              <Text strong>
                <a href={linkPath}>{item.sourceItemName}</a>
              </Text>
            ) : (
              <Text strong>{item.sourceItemName || "-"}</Text>
            )}
          </Space>
          <Text>
            {t("table.activity")}: {item.name}
          </Text>
          <Text type="secondary">
            {t("table.startDate")}:{" "}
            {item.startDate
              ? new Date(item.startDate).toLocaleDateString()
              : "-"}
          </Text>
          <Tag color={item.state === "Active" ? "green" : "default"}>
            {item.state}
          </Tag>
        </Space>
      </Card>
    );
  };

  return (
    <List
      title={
        <Space>
          {t("workflow.inbasket")}
          <PageTourButton tourId="workflow-inbasket" size="small" iconOnly />
        </Space>
      }
      contentProps={{ style: { overflow: "hidden" } }}
      headerButtons={
        <Space data-tour="task-filters" size={isMobile ? 4 : 8}>
          <Segmented
            data-tour="view-mode-toggle"
            value={viewMode}
            onChange={(value) => handleViewModeChange(value as ViewMode)}
            options={[
              {
                value: "card",
                icon: <AppstoreOutlined />,
              },
              {
                value: "table",
                icon: <BarsOutlined />,
              },
            ]}
            size={isMobile ? "small" : "middle"}
          />
          {viewMode === "table" && (
            <Dropdown menu={columnSettingsMenu} trigger={["click"]}>
              <Tooltip title={t("table.columnSettings", "Cài đặt cột")}>
                <Button
                  data-tour="column-settings"
                  icon={<SettingOutlined />}
                  size={isMobile ? "small" : "middle"}
                >
                  {!isMobile && t("table.columns", "Cột")}
                </Button>
              </Tooltip>
            </Dropdown>
          )}
        </Space>
      }
    >
      {/* Search toolbar */}
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          overflow: "hidden",
        }}
      >
        {isMobile ? (
          <Input
            placeholder={t("table.searchPlaceholder", "Tìm kiếm...")}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={onSearchChange}
            allowClear
            style={{ flex: 1, minWidth: 0 }}
            size="small"
          />
        ) : (
          <Space.Compact>
            <Select
              value={searchField}
              onChange={onSearchFieldChange}
              style={{ minWidth: 120 }}
              options={searchFieldOptions}
              popupMatchSelectWidth={false}
            />
            <Input
              placeholder={t(
                "table.searchPlaceholder",
                "Nhập thông tin để tìm kiếm...",
              )}
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={onSearchChange}
              allowClear
              style={{ width: 250 }}
            />
          </Space.Compact>
        )}
      </div>

      {viewMode === "table" ? (
        <div data-tour="inbasket-list">
          <Table
            dataSource={inbasketData?.items || []}
            columns={filteredColumns}
            rowKey="id"
            loading={isLoading}
            scroll={{ x: isMobile ? "max-content" : 1200 }}
            size={isMobile ? "small" : "middle"}
            pagination={{
              current: page,
              pageSize,
              total: inbasketData?.totalCount || 0,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
              showSizeChanger: !isMobile,
              showQuickJumper: !isMobile,
              simple: isMobile,
              showTotal: isMobile
                ? undefined
                : (total) => t("workflow.totalItems", { total }),
            }}
          />
        </div>
      ) : (
        <div style={{ overflow: "hidden" }}>
          {isLoading ? (
            <Card loading style={{ marginBottom: 16 }} />
          ) : (
            <>
              {(inbasketData?.items || []).map(renderTaskCard)}
              {(inbasketData?.items?.length || 0) === 0 && (
                <Text type="secondary">
                  {t("table.noData", "Không có dữ liệu")}
                </Text>
              )}
            </>
          )}
        </div>
      )}

      <Modal
        title={
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            {t("workflow.activityCompletion")}
          </Title>
        }
        open={completeModalVisible}
        onOk={handleComplete}
        onCancel={handleCloseModal}
        confirmLoading={completing}
        okButtonProps={{ disabled: !selectedPath }}
        okText={t("actions.complete")}
        cancelText={t("actions.cancel")}
        width={isMobile ? "95%" : 520}
        centered={isMobile}
      >
        {/* Workflow Info Section */}
        <Descriptions
          column={1}
          size="small"
          style={{ marginBottom: 16 }}
          labelStyle={{ fontWeight: 600, width: 100 }}
        >
          <Descriptions.Item label={t("workflow.workflowName")}>
            <Text strong style={{ color: token.colorPrimary }}>
              {selectedItem?.sourceItemName || "-"}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label={t("workflow.activity")}>
            {selectedItem?.name || "-"}
          </Descriptions.Item>
          {selectedItem?.assignedTo && (
            <Descriptions.Item label={t("table.assignedTo")}>
              {selectedItem.assignedTo}
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* Instructions Section */}
        {selectedItem?.instructions && (
          <>
            <Divider style={{ margin: "12px 0" }} />
            <div style={{ marginBottom: 16 }}>
              <Text strong>{t("table.instructions")}:</Text>
              <div
                style={{
                  marginTop: 8,
                  padding: "8px 12px",
                  backgroundColor: token.colorFillQuaternary,
                  borderRadius: 4,
                  borderLeft: `3px solid ${token.colorPrimary}`,
                }}
              >
                <Text>{selectedItem.instructions}</Text>
              </div>
            </div>
          </>
        )}

        <Divider style={{ margin: "12px 0" }} />

        {/* Vote Section */}
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            {t("workflow.vote")}:
          </Text>
          <Select
            style={{ width: "100%" }}
            placeholder={t("workflow.selectPathPlaceholder")}
            value={selectedPath}
            onChange={setSelectedPath}
            loading={pathsLoading}
            options={paths.map((path) => ({
              label: path.label || path.name,
              value: path.id,
            }))}
          />
        </div>

        {/* Comments Section */}
        <div>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            {t("workflow.comments")}:
          </Text>
          <TextArea
            rows={3}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder={t("workflow.commentsPlaceholder")}
          />
        </div>
      </Modal>
    </List>
  );
};
